import {ApolloProvider} from '@apollo/client';
import {useMemo, useRef} from 'react';
import type {ReactNode} from 'react';
import {useAuth, useOrganizationIdentifier} from '~/lib/auth';
import {createViewerApolloClient} from '~/lib/graphql/viewer-apollo-client';

// Mounted above the whole panel, not inside the pages view: switching views
// would otherwise destroy the viewer cache and refetch on return.
export const PagesProvider = ({children}: {children: ReactNode}) => {
  const {isPreview, isAnonymous} = useAuth();
  const organizationIdentifier = useOrganizationIdentifier();

  const clientCache = useRef<
    Map<string, ReturnType<typeof createViewerApolloClient>>
  >(new Map());

  // Dispose only on organization change, never in an effect cleanup: under
  // StrictMode that stops the still-mounted client and kills its watchers.
  const client = useMemo(() => {
    if (!organizationIdentifier || isAnonymous) {
      return null;
    }
    if (!clientCache.current.has(organizationIdentifier)) {
      clientCache.current.forEach((entry) => entry.dispose());
      clientCache.current.clear();
      clientCache.current.set(
        organizationIdentifier,
        createViewerApolloClient(organizationIdentifier),
      );
    }
    return clientCache.current.get(organizationIdentifier)!.client;
  }, [isAnonymous, organizationIdentifier]);

  if (isPreview || !client) {
    return <>{children}</>;
  }

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};
